<template>
  <div>
    <header class="db-page-head">
      <div>
        <h2 class="db-page-title">Sitios de interés</h2>
        <p class="db-page-subtitle">Enlaces que aparecen en la sección "Sitios de interés" de la portada.</p>
      </div>
      <div class="db-actions">
        <button type="button" class="db-btn" @click="showModal()">
          <span class="material-symbols-outlined">add_link</span> Nuevo enlace
        </button>
      </div>
    </header>

    <section class="db-card">
      <div v-if="loading" class="db-card-body ui-skeletons">
        <div v-for="n in 4" :key="n" class="db-skeleton" style="height: 48px"></div>
      </div>

      <div v-else-if="!enlacesInteres.length" class="db-empty">
        <span class="material-symbols-outlined">link</span>
        <p>No hay enlaces todavía.</p>
        <button type="button" class="db-btn db-btn--sm" @click="showModal()">Añadir el primero</button>
      </div>

      <div v-else class="db-table-wrap">
        <table class="db-table">
          <thead>
            <tr>
              <th>Enlace</th>
              <th class="db-hide-sm">Posición</th>
              <th>Visible</th>
              <th class="db-col-actions"><span class="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in enlacesInteres" :key="e.id">
              <td>
                <div class="ui-link">
                  <img :src="favicon(e.url)" alt="" class="ui-favicon" loading="lazy" @error="$event.target.style.visibility = 'hidden'" />
                  <span class="ui-link-text">
                    <span class="db-cell-title">{{ dominio(e.url) }}</span>
                    <a :href="e.url" target="_blank" rel="noopener" class="db-cell-sub ui-url">{{ e.url }}</a>
                  </span>
                </div>
              </td>
              <td class="db-hide-sm">{{ e.posicion }}</td>
              <td>
                <label class="db-switch" :title="esVisible(e) ? 'Ocultar' : 'Mostrar'">
                  <input
                    type="checkbox"
                    :checked="esVisible(e)"
                    :disabled="cambiando === e.id"
                    :aria-label="(esVisible(e) ? 'Ocultar ' : 'Mostrar ') + dominio(e.url)"
                    @change="cambiarVisible(e)"
                  />
                  <span class="db-switch-track"></span>
                </label>
              </td>
              <td class="db-col-actions">
                <button type="button" class="db-icon-btn" title="Editar" aria-label="Editar enlace" @click="showModal(e.id)">
                  <span class="material-symbols-outlined">edit</span>
                </button>
                <button type="button" class="db-icon-btn db-icon-btn--danger" title="Eliminar" aria-label="Eliminar enlace" @click="deleteURL(e)">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <modal-url :url="getURL" modalId="editorDeURL" @obtenerEnlacesInteres="obtenerEnlacesInteres" />
  </div>
</template>

<script>
import axios from "@/directives/axios.js";
import modalUrl from "@/components/dashboard/modals/ModalUrl.vue";
import { confirmar, avisar, avisarError } from "@/directives/dbDialogs.js";

export default {
  name: "vistaUrlInteres",
  components: {
    modalUrl,
  },
  data() {
    return {
      enlacesInteres: [],
      getURL: undefined,
      loading: true,
      cambiando: null,
    };
  },
  methods: {
    esVisible(e) {
      return e.activo == 1 || e.activo === true;
    },
    dominio(url) {
      try {
        return new URL(url).hostname.replace(/^www\./, "");
      } catch (err) {
        return url;
      }
    },
    favicon(url) {
      try {
        return `https://www.google.com/s2/favicons?sz=64&domain=${new URL(url).hostname}`;
      } catch (err) {
        return "";
      }
    },
    showModal(id) {
      this.getURL = id ? this.enlacesInteres.find((element) => element.id === id) : undefined;
      this.$nextTick(() => this.$bvModal.show("editorDeURL"));
    },
    async cambiarVisible(e) {
      this.cambiando = e.id;
      try {
        const respuesta = await axios.post("/enlaces/editar", { id: e.id, url: e.url, visible: !this.esVisible(e) });
        this.obtenerEnlacesInteres(respuesta);
        avisar(this, `${this.dominio(e.url)} ${this.esVisible(e) ? "ya no aparece" : "ya aparece"} en la portada.`, "success", this.esVisible(e) ? "Enlace oculto" : "Enlace visible");
      } catch (error) {
        avisarError(this, error, "No se pudo cambiar la visibilidad.");
        this.obtenerEnlacesInteres();
      } finally {
        this.cambiando = null;
      }
    },
    async deleteURL(enlace) {
      const ok = await confirmar(this, `Se eliminará el enlace a ${this.dominio(enlace.url)}.`, {
        titulo: "Eliminar enlace",
        ok: "Eliminar",
        peligro: true,
      });
      if (!ok) return;
      try {
        const respuesta = await axios.delete(`/enlaces/eliminar?id=${enlace.id}`);
        this.obtenerEnlacesInteres(respuesta);
        avisar(this, `Se ha eliminado el enlace a ${this.dominio(enlace.url)}.`, "success", "Enlace eliminado");
      } catch (error) {
        avisarError(this, error, "No se pudo eliminar el enlace.");
      }
    },
    // Mismo orden que la API y la portada: posición ascendente
    ordenar(lista) {
      return [...(lista || [])].sort((a, b) => (Number(a.posicion) || 0) - (Number(b.posicion) || 0));
    },
    // Acepta la respuesta de una operación (que ya trae la lista) o, si no, la vuelve a pedir
    async obtenerEnlacesInteres(respuesta) {
      if (respuesta && respuesta.data && respuesta.data.todosEnlaces) {
        this.enlacesInteres = this.ordenar(respuesta.data.todosEnlaces);
        return;
      }
      try {
        const { data } = await axios.get("/enlaces/todosEnlaces");
        this.enlacesInteres = this.ordenar(data.todosEnlaces);
      } catch (error) {
        avisarError(this, error, "No se pudieron cargar los enlaces.");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.obtenerEnlacesInteres();
  },
};
</script>

<style scoped>
.ui-skeletons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ui-link {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ui-favicon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 4px;
  border: 1px solid var(--sd-border);
  border-radius: 8px;
  background: #fff;
}

.ui-link-text {
  min-width: 0;
}

.ui-url {
  display: block;
  max-width: 420px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--sd-muted);
  text-decoration: none;
}

.ui-url:hover {
  color: var(--sd-primary);
  text-decoration: underline;
}
</style>
